import React, { useState, useEffect, useRef } from "react";
import { getTestGroups, associateTestCasesWithTestGroup } from "../../utils/api/testgroup_apis";
import { STATUS } from "../../utils/api/sharedfunction";
import { useSelector } from "react-redux";
import ModalWrapper from "../ModalWrapper";
import CommonFooter from "../FormComponent/CommonFooter";
import { FormSchemas, FormWrapper } from "../FormComponent";
import CreateTestGroupModal from "../TestGroupLibrary/CreateTestGroupModal";

const AddTCToTestGroupModal = ({ isOpen, onClose, onError, onAdd, testCaseIds }) => {
  const [testGroups, setTestGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const selectedProject = useSelector(state => state.projects.selectedProject);
  const formRef = useRef(null);

  useEffect(() => {
    if (isOpen && selectedProject?.id) {
      setSearchValue("");
      if (formRef.current) {
        formRef.current.setValue('testGroupId', '');
        formRef.current.clearErrors('testGroupId');
      }
      fetchGroups();
    }
    // eslint-disable-next-line
  }, [isOpen, selectedProject?.id]);
  
  const handleFormSubmit = async () => {
    if (formRef.current) await formRef.current.submit();
  };
  const fetchGroups = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getTestGroups({ page: 1, pageSize: 10000 });
      if (response.status === STATUS.SUCCESS) {
        setTestGroups(response.data?.rows || []);
      } else {
        setError(response.error || "Error fetching test groups");
        setTestGroups([]);
      }
    } catch (err) {
      setError("Error fetching test groups");
      setTestGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAdd = async (data) => {
    try {
      const testGroupId = data.testGroupId;
      // Check if user selected "Create Test Group" option
      if (testGroupId === "CREATE_NEW") {
        setShowCreateModal(true);
        return;
      }
      const response = await associateTestCasesWithTestGroup({ testGroupId, testCaseIds });
      if (response.status === STATUS.SUCCESS) {
        onAdd(response);
        onClose();
      } else {
        onError(response.error || "Failed to add to test group", "error");
      }
    } catch (error) {
      onError(error?.message || "Failed to add to test group", "error");
    }
  };

  const handleCreateTestGroupSuccess = async (newTestGroup) => {
    setShowCreateModal(false);
    await fetchGroups();
    setSearchValue("");
    if (formRef.current && newTestGroup) {
      setTimeout(() => {
        formRef.current.setValue('testGroupId', newTestGroup.id);
      }, 100);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const handleInputChange = (value) => {
    setSearchValue(value);
  };

  const handleTestGroupChange = (e) => {
    const value = e.target.value;
    // If user selected "Create Test Group" option, open modal immediately
    if (value === "CREATE_NEW") {
      setShowCreateModal(true);

      if (formRef.current) {
        formRef.current.setValue('testGroupId', '');
        formRef.current.clearErrors('testGroupId');
      }
      return;
    }
    if (!value) {
      setSearchValue("");
    }
    // For normal selections, let the form handle it normally
    // The Controller's onChange will be called automatically
    if (formRef.current) {
      formRef.current.setValue('testGroupId', value);
      // Clear any validation errors
      formRef.current.clearErrors('testGroupId');
    }
  };

  // Filter options based on search value and add "Create Test Group" option if no matches
  const getOptions = () => {
    const baseOptions = testGroups.map((group) => ({
      value: group.id,
      label: [group.key] + ' - ' + group.name,
    }));

    // If there's a search value, filter options
    if (searchValue && searchValue.trim()) {
      const filtered = baseOptions.filter(option =>
        option.label.toLowerCase().includes(searchValue.toLowerCase())
      );
      
      // If no matches found, add "Create Test Group" option
      if (filtered.length === 0) {
        return [
          {
            value: "CREATE_NEW",
            label: `Create Test Group "${searchValue}"`,
            isCreateOption: true,
          }
        ];
      }
      return filtered;
    }
    
    return baseOptions;
  };

  const buttons = [
    {
      label: "Cancel",
      variant: "outline",
      onClick: onClose,
      className: "primary",
    },
    {
      label: "Add",
      variant: 'primary',
      className: "white bg-blue600",
      onClick: handleFormSubmit,
    },
  ];

  const fields = [
    {
      name: 'testGroupId',
      type: 'autosuggest',
      label: 'Test Group',
      required: true,
      options: getOptions(),
      helperText: error || "",
      isLoading: loading,
      onInputChange: handleInputChange,
      onChange: handleTestGroupChange,
    },
  ];

  return (
    <>
      <ModalWrapper
        isOpen={isOpen}
        onClose={onClose}
        title="Add Test Cases to Test Group"
        size="small"
        footer={
          <CommonFooter
            buttons={buttons}
            align="right"
          />
        }
        children={
          <FormWrapper
            ref={formRef}
            schema={FormSchemas.addToTestGroup}
            fields={fields}
            onSubmit={handleConfirmAdd}
            defaultValues={{
              testGroupId: '',
            }}
          />
        }
      />
      <CreateTestGroupModal
        open={showCreateModal}
        onClose={handleCloseCreateModal}
        onSuccess={handleCreateTestGroupSuccess}
        projectId={selectedProject?.id}
        existingTestGroups={testGroups}
        initialValues={{ name: searchValue }}
      />
    </>
  );
};

export default AddTCToTestGroupModal; 